import type { CSSProperties } from 'react';

export type TextStyleTokens = {
  primary: string;
  secondary: string;
  muted: string;
};

export type TextStyleDefinition = {
  label: string;
  style: (tokens: TextStyleTokens) => CSSProperties;
};

export type TextStyleGroup = Record<string, TextStyleDefinition>;

const tint = (color: string, percent: number) =>
  `color-mix(in srgb, ${color} ${percent}%, transparent)`;

/**
 * Styles that can be assigned to a selected piece of text.
 * Keys are stored on the lexical text node (`node.$`), so renaming them breaks existing content.
 * Values within one group are mutually exclusive, values from different groups can be combined.
 */
export const textStyles: Record<string, TextStyleGroup> = {
  color: {
    primary: {
      label: 'Color: Primary',
      style: ({ primary }) => ({ color: primary }),
    },
    secondary: {
      label: 'Color: Secondary',
      style: ({ secondary }) => ({ color: secondary }),
    },
    muted: {
      label: 'Color: Muted',
      style: ({ muted }) => ({ color: muted }),
    },
  },
  highlight: {
    primary: {
      label: 'Highlight: Primary',
      style: ({ primary }) => ({
        backgroundColor: tint(primary, 20),
        borderRadius: '0.2em',
        padding: '0 0.15em',
      }),
    },
    secondary: {
      label: 'Highlight: Secondary',
      style: ({ secondary }) => ({
        backgroundColor: tint(secondary, 25),
        borderRadius: '0.2em',
        padding: '0 0.15em',
      }),
    },
    marker: {
      label: 'Highlight: Marker',
      style: ({ secondary }) => ({
        backgroundImage: `linear-gradient(transparent 60%, ${tint(secondary, 45)} 60%)`,
      }),
    },
  },
  size: {
    small: {
      label: 'Size: Small',
      style: () => ({ fontSize: '0.875em' }),
    },
    large: {
      label: 'Size: Large',
      style: () => ({ fontSize: '1.25em' }),
    },
    xlarge: {
      label: 'Size: Extra large',
      style: () => ({ fontSize: '1.5em' }),
    },
  },
  letterCase: {
    uppercase: {
      label: 'Case: Uppercase',
      style: () => ({ textTransform: 'uppercase', letterSpacing: '0.05em' }),
    },
  },
};
